import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { register } from '../api';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { token, loading, saveSession } = useAuth();

  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '', address: '', description: '',
    business_type: '',
  });
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState(null);

  // Already logged in → go straight to dashboard
  useEffect(() => {
    if (!loading && token && !success) navigate('/vendor/dashboard', { replace: true });
  }, [loading, token, navigate, success]);

  const field = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_type)     { setError('Please select your business type.'); return; }
    if (!form.name.trim())       { setError('Shop name is required.'); return; }
    if (!form.email.trim())      { setError('Email is required.'); return; }
    if (form.password.length < 6){ setError('Password must be at least 6 characters.'); return; }

    setError('');
    setBusy(true);
    try {
      const res = await register(form);
      saveSession(res.token, res.business);
      setSuccess({ businessId: res.businessId, name: res.business.name });
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;

  // ── Success screen ─────────────────────────────────────────────────────────
  if (success) {
    const shopUrl = `${window.location.origin}/shop/${success.businessId}`;
    return (
      <div className="auth-page">
        <div className="auth-card success-card">
          <div className="success-icon">✓</div>
          <h2>Welcome, {success.name}!</h2>
          <p className="auth-sub">Your shop is ready. Save your Business ID — you need it to log in.</p>

          <div className="biz-id-display">
            <div className="biz-id-label">Your Business ID</div>
            <div className="biz-id-value">{success.businessId}</div>
          </div>

          <div className="qr-url-box">
            <div className="qr-url-label">Customer Menu Link (share this / make a QR code)</div>
            <div className="qr-url-value">{shopUrl}</div>
          </div>

          <button className="auth-btn" onClick={() => navigate('/vendor/dashboard')}>
            Go to Dashboard →
          </button>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────────────────
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo"><Logo size="lg" /></div>
        <h2 className="auth-title">Register Your Shop</h2>
        <p className="auth-sub">Free. Takes 30 seconds.</p>

        <form onSubmit={submit}>
          {error && <div className="auth-error">{error}</div>}

          <div className="auth-field">
            <label>Business Type *</label>
            <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
              {[
                { value: 'restaurant', emoji: '🍽️', label: 'Restaurant / Café / Dhaba', sub: 'Table orders, takeaway, delivery' },
                { value: 'store',      emoji: '🛒', label: 'Kirana / Grocery / Retail',  sub: 'Home delivery & pick-up orders' },
              ].map(({ value, emoji, label, sub }) => {
                const selected = form.business_type === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, business_type: value }))}
                    style={{
                      flex: 1, padding: '12px 8px', borderRadius: 12, cursor: 'pointer',
                      border: `2px solid ${selected ? '#FF6B35' : '#e5e7eb'}`,
                      background: selected ? '#fff5f0' : '#fafafa',
                      textAlign: 'center', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ fontSize: 24 }}>{emoji}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: selected ? '#FF6B35' : '#333', marginTop: 4 }}>{label}</div>
                    <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="auth-field">
            <label>Shop Name *</label>
            <input className="auth-input" placeholder="e.g. Ramesh Chai Stall"
              value={form.name} onChange={field('name')} />
          </div>
          <div className="auth-field">
            <label>Email *</label>
            <input className="auth-input" type="email" placeholder="you@example.com"
              value={form.email} onChange={field('email')} />
          </div>
          <div className="auth-field">
            <label>Password * (min 6 characters)</label>
            <input className="auth-input" type="password" placeholder="••••••••"
              value={form.password} onChange={field('password')} />
          </div>
          <div className="auth-field">
            <label>Phone (optional)</label>
            <input className="auth-input" placeholder="+91 98765 43210"
              value={form.phone} onChange={field('phone')} />
          </div>
          <div className="auth-field">
            <label>Address (optional)</label>
            <input className="auth-input" placeholder="Shop address or area"
              value={form.address} onChange={field('address')} />
          </div>
          <div className="auth-field">
            <label>Shop Description (optional)</label>
            <input className="auth-input" placeholder="e.g. Best chai since 1995"
              value={form.description} onChange={field('description')} />
          </div>

          <button className="auth-btn" type="submit" disabled={busy}>
            {busy ? 'Registering…' : 'Register Shop →'}
          </button>
        </form>

        <p className="auth-footer">
          Already registered? <Link to="/vendor/login">Log in</Link>
        </p>
      </div>
    </div>
  );
}
